import { DecimalPipe } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { LoadingSkeletonComponent, ToolTipDirective } from '@softline/ui-core';
import {
  DsProgressBarDensity,
  DsProgressBarSegment,
  DsProgressBarSeverity,
  DsProgressBarVariant,
} from './ds-progress-bar.types';

@Component({
  selector: 'soft-ds-progress-bar',
  templateUrl: './ds-progress-bar.component.html',
  styleUrls: ['./ds-progress-bar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DecimalPipe, LoadingSkeletonComponent, ToolTipDirective],
  host: {
    '[class]': 'hostClasses()',
    role: 'progressbar',
    '[attr.aria-label]': 'ariaLabel()',
    '[attr.aria-labelledby]': 'ariaLabelledby()',
    '[attr.aria-describedby]': 'ariaDescribedby()',
    '[attr.aria-valuemin]': 'ariaValueMin()',
    '[attr.aria-valuenow]': 'ariaValueNow()',
    '[attr.aria-valuemax]': 'ariaValueMax()',
    '[attr.aria-valuetext]': 'resolvedAriaValueText()',
    '[attr.aria-busy]': 'loading() ? "true" : null',
  },
})
export class DsProgressBarComponent {
  readonly variant = input<DsProgressBarVariant>('percent');
  /** Bar thickness. `lg`/`xl` are for standalone/prominent bars; `default`
      preserves the historical thickness for inline/compact contexts. */
  readonly density = input<DsProgressBarDensity>('default');
  readonly loading = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  readonly ariaLabel = input<string | null>(null, { alias: 'aria-label' });
  readonly ariaLabelledby = input<string | null>(null, { alias: 'aria-labelledby' });
  readonly ariaDescribedby = input<string | null>(null, { alias: 'aria-describedby' });
  readonly ariaValueText = input<string | null>(null, { alias: 'aria-valuetext' });

  // --- percent variant inputs ---
  /** Current value. Combined with `max` to compute the fill percent. */
  readonly value = input<number | null>(null);
  /** Upper bound for `value`. Defaults to 100 (i.e. `value` is already a percent). */
  readonly max = input<number>(100);
  /** Severity-driven fill for the percent variant. Ignored when `color` is set. */
  readonly severity = input<DsProgressBarSeverity | null>(null);
  /** Explicit fill color (CSS color string). Wins over severity. */
  readonly color = input<string | null>(null);
  /** Optional reference tick along the track (percent of the *visible scale*). */
  readonly markerPercent = input<number | null>(null);
  /** Optional horizontal scale factor. Use < 1 to leave headroom past the marker. */
  readonly scale = input<number>(1);

  // --- segmented variant inputs ---
  readonly segments = input<readonly DsProgressBarSegment[]>([]);
  /** When true (segmented variant), renders a legend below the bar with one
      row per segment: colored dot + count + label. */
  readonly showLegend = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  /** Number of skeleton legend rows to render while `loading`. Only honored
      when `showLegend` is true. */
  readonly legendPlaceholderCount = input<number>(4);
  /** Extra class(es) applied to the legend `<ul>` (segmented variant, `showLegend`).
      When set, the built-in 2-column grid layout is omitted so the supplied class
      fully controls layout — pair with your own display/columns rules. */
  readonly legendClass = input<string>('');

  /** When true, each segment and legend row becomes a button that emits
      `(segmentClick)` with the corresponding segment. Hover/focus styling
      surfaces the affordance. */
  readonly clickableSegments = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  readonly segmentClick = output<DsProgressBarSegment>();

  protected readonly legendPlaceholderRange = computed(() =>
    Array.from({ length: Math.max(0, this.legendPlaceholderCount()) })
  );

  protected readonly hostClasses = computed(
    () => `soft-ds-progress-bar-host variant-${this.variant()} density-${this.density()}`
  );

  /** Raw fill percent (`value / max * 100`), unclamped. Combined with `scale`
      in the template to compute the rendered width. Negative values clamp to 0;
      the rendered width is clamped to 100% so over-budget values can still be
      shown by setting `scale < 1` (e.g. `scale = 100/150` for a 0-150% bar). */
  protected readonly percentFill = computed(() => {
    const v = this.value();
    const m = this.max();
    if (v == null || !m || m <= 0) return 0;
    return Math.max(0, (v / m) * 100);
  });

  protected readonly ariaValueNow = computed(() => {
    if (this.loading() || this.variant() !== 'percent' || this.value() === null) {
      return null;
    }

    return Math.min(100, Math.max(0, this.percentFill()));
  });

  protected readonly ariaValueMin = computed(() => {
    if (this.ariaValueNow() === null) return null;
    return 0;
  });

  protected readonly ariaValueMax = computed(() => {
    if (this.ariaValueNow() === null) return null;
    return 100;
  });

  protected readonly percentFillStyle = computed(() => {
    const explicit = this.color();
    if (explicit) return explicit;
    const sev = this.severity();
    if (sev) return `var(--ds-progress-bar-fill-${sev})`;
    return 'var(--ds-progress-bar-fill-success)';
  });

  protected readonly visibleSegments = computed(() =>
    this.segments().filter((s) => (s.value ?? 0) > 0)
  );

  protected readonly resolvedAriaValueText = computed(() => {
    if (this.loading()) return null;

    const explicit = this.ariaValueText();
    if (explicit) return explicit;
    if (this.variant() !== 'segmented') return null;

    const summary = this.visibleSegments()
      .map((s) => (s.label ? `${s.label}: ${s.value ?? 0}` : `${s.value ?? 0}`))
      .join(', ');

    return summary || null;
  });

  /** Total weight of all visible segments — used to convert each segment's
      value to a percentage of the track. */
  protected readonly segmentsTotal = computed(() =>
    this.visibleSegments().reduce((sum, s) => sum + (s.value ?? 0), 0)
  );

  protected segmentPercent(s: DsProgressBarSegment): number {
    const total = this.segmentsTotal();
    if (total <= 0) return 0;
    return ((s.value ?? 0) / total) * 100;
  }

  protected segmentBackground(s: DsProgressBarSegment): string {
    if (s.color) return s.color;
    if (s.severity) return `var(--ds-progress-bar-fill-${s.severity})`;
    return 'var(--ds-progress-bar-fill-info)';
  }
}
